import React, { useEffect, useState } from 'react';
import { TableCell } from '@mui/material';
import { useDispatch } from 'react-redux';

// Components
import Button from '@/main/components/shared/button/button';
import Box from '@/main/components/shared/box';
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import KogInputTextControlledNew from '@/main/components/shared/inputText/inputTextControlledNew';
import BoxGrid from '@/main/components/shared/boxGrid/boxGrid';
import StatusComponent from '@/main/components/shared/status/statusComponent';
import { AreaButton, KOGAreaButtons } from '@/main/components/shared/styles';
import { KOGTableRow } from '@/main/components/shared/boxGrid/styles';
import ResultNotFoundComponent from '@/main/components/shared/resultNotFound/resultNotFoundComponent';
import LoadingComponent from '@/main/components/shared/loading/loadingComponent';
import ContainerScreen from '@/main/components/shared/containerScreen/containerLayout';

// Styles
import { useNavigate } from 'react-router-dom';
import { RoutesConstants } from '@/main/routes/routesConstants';

// Api's
import { getCountriesUser, useCountryReducer } from '@/main/store/country';
import {
  getStatusRegister,
  useStatusRegisterReducer
} from '@/main/store/statusRegister';

import {
  getSuppliers,
  resetReducerSuppliers,
  useSupplierReducer
} from '@/main/store/supplier';
import { KeyValue } from '@/main/models/common';
import { SupplierFilterRequest } from '@/main/models/request/supplierRequest';
import { useForm } from 'react-hook-form';
import { SupplierDataResponse } from '@/main/models/response';
import { supplierApi } from '@/main/api';
import { PaginationEnum } from '@/main/services/enum/PaginationEnum';
import { StatusRegisterEnum } from '@/main/services/enum/StatusRegisterEnum';
import { HeadQuarterBranchFilterEnum } from '@/main/services/enum/HeadQuarterBranchFilterEnum';
import {
  getDefaultMessageResultsFound,
  getTranslateByKey,
  removeEmptyValues
} from '@/main/services/utils';
import * as S from './styles';
import { withQueryparams } from '@/main/components/core/queryParams/queryparams';
import useFormQueryParams from '@/main/hooks/useformQueryParams';
import { toast } from 'react-toastify';
import { userIsAdmin, userIsInsurer } from '@/main/services/utilsUser';

export interface ISearchSupplierForm {
  country: KeyValue | null;
  company: KeyValue | null;
  rfc: string | null;
  statusRegister: KeyValue | null;
}

interface IGridData {
  data: SupplierDataResponse[];
  totalItems: number;
  isLoading: boolean;
}

interface IPropsSupplierSearchGrid {
  gridData: IGridData;
  formFilters: SupplierFilterRequest | null;
  setFormFilters: (formFilters: SupplierFilterRequest | null) => void;
  getGridData: (formFilters: SupplierFilterRequest) => void;
  hasShowMore: boolean;
  pageIndex: number;
  setPageIndex: (index: number) => void;
  isLoadingInfiniteScroll: boolean;
}

const SupplierSearchGrid = ({
  gridData,
  formFilters,
  setFormFilters,
  getGridData,
  hasShowMore,
  pageIndex,
  setPageIndex,
  isLoadingInfiniteScroll
}: IPropsSupplierSearchGrid) => {
  const rowHeaders = [
    getTranslateByKey('information.rfc'),
    getTranslateByKey('information.company'),
    getTranslateByKey('information.tradeName'),
    `${getTranslateByKey('information.state')}/${getTranslateByKey(
      'information.city'
    )}`,
    getTranslateByKey('information.status')
  ];
  const navigate = useNavigate();

  function handleRowBoxGrid(supplier: SupplierDataResponse) {
      if(supplier.hasOtherCompanyRelation){
        toast.error( getTranslateByKey('information.companyAssociatedWithAnotherInsurance', true));
        return;
      }
      navigate(
      `${RoutesConstants.registrationRegisterSupplier}?supplierGuid=${supplier.guid}`
    );
  }

  function onLoadSearchGrid() {
    const index = pageIndex + PaginationEnum.PageIndexIntial;
    setPageIndex(index);

    const formFiltersOnLoadSearchGrid = formFilters;
    if (formFiltersOnLoadSearchGrid) {
      formFiltersOnLoadSearchGrid.pageIndex = index;

      setFormFilters(formFiltersOnLoadSearchGrid);
      getGridData(formFiltersOnLoadSearchGrid);
    }
  }

  function renderAddress(supplier: SupplierDataResponse) {
    if (supplier.addresses && supplier.addresses.length > 0) {
      const state = supplier.addresses[0]?.municipality?.city?.state?.name;
      const city = supplier.addresses[0]?.municipality?.city?.name;
      if (state && city) {
        return `${state}/${city}`;
      }
    }
    return <></>;
  }

  const titleGrid = getDefaultMessageResultsFound(
    gridData.data.length,
    gridData.totalItems
  );

  if (gridData.isLoading) return <LoadingComponent />;
  if (gridData.data.length === 0 && formFilters !== null)
    return <ResultNotFoundComponent styles={{ marginTop: 10 }} />;
  if (gridData.data.length === 0 && !gridData.isLoading) return null;

  return (
    <BoxGrid
      title={titleGrid}
      rowHeaders={rowHeaders}
      infiniteScrollConfig={{
        dataLength: gridData.data.length,
        fetchData: () => onLoadSearchGrid(),
        hasMore: hasShowMore,
        isLoading: isLoadingInfiniteScroll
      }}>
      {gridData.data.map((supplier: SupplierDataResponse) => {
        return (
          <KOGTableRow
            key={supplier.guid}
            onClick={() => handleRowBoxGrid(supplier)}
            id={'id-searchSupplier-tableRow-result-' + supplier.companyName}
          >
            <TableCell width="20%">{supplier.registrationNumber}</TableCell>
            <TableCell>
              <div style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {supplier.companyName}
              </div>
            </TableCell>
            <TableCell>
              <div style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {supplier.tradeName}
              </div>
            </TableCell>
            <TableCell width="25%">{renderAddress(supplier)}</TableCell>
            <TableCell width="10%">
              <StatusComponent status={supplier?.statusRegister} />
            </TableCell>
          </KOGTableRow>
        );
      })}
    </BoxGrid>
  );
};

function SupplierScreen() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // State & reducers
  const countryReducer = useCountryReducer();
  const statusRegisterReducer = useStatusRegisterReducer();
  const supplierReducer = useSupplierReducer();

  const optionDefaultCountryDrop =
    countryReducer.countriesUser.data !== null &&
      countryReducer.countriesUser.data.length === 1
      ? countryReducer.countriesUser.data[0]
      : null;

  const optionDefaultSupplierDrop =
    supplierReducer.suppliers.data !== null &&
      supplierReducer.suppliers.data.length === 1
      ? supplierReducer.suppliers.data[0]
      : null;

  const INITIAL_FORM_STATE: ISearchSupplierForm = {
    country: optionDefaultCountryDrop,
    company: optionDefaultSupplierDrop,
    rfc: '',
    statusRegister: null
  };
  const {
    control,
    handleSubmit,
    customHandleSubmit,
    setValue,
    formState: { errors },
    reset,
    getValues
  } = useFormQueryParams<ISearchSupplierForm>({
    mode: 'onChange',
    defaultValues: INITIAL_FORM_STATE,
    onSubmit: onSubmit,
    mapDefaultValues: {
      company: supplierReducer.suppliers,
      country: countryReducer.countriesUser,
      statusRegister: statusRegisterReducer.statusRegister
    }
  });
  const [showFilters, setShowFilters] = useState<boolean>(true);
  const INITIAL_GRID_STATE: IGridData = {
    totalItems: 0,
    isLoading: false,
    data: []
  };
  const [gridData, setGridData] = useState<IGridData>(INITIAL_GRID_STATE);
  const [formFilters, setFormFilters] = useState<SupplierFilterRequest | null>(
    null
  );
  const [hasShowMore, setHasShowMore] = useState<boolean>(false);
  const [pageIndex, setPageIndex] = useState<number>(
    PaginationEnum.PageIndexIntial
  );
  const [isLoadingInfiniteScroll, setIsLoadingInfiniteScroll] = useState<
    boolean
  >(false);

  const isDisabledSupplierDrop = () =>
    supplierReducer.suppliers.data === null || getValues().country === null;

  // UseEffect's
  // Initial load
  useEffect(() => {
    dispatch(getCountriesUser());
    dispatch(getStatusRegister(true));

    return () => {
      dispatch(resetReducerSuppliers());
    };
  }, []);

  useEffect(() => {
    // Call API's related by country default
    if (optionDefaultCountryDrop) {
      callSupplierApiDomain(optionDefaultCountryDrop);
    }
  }, [optionDefaultCountryDrop]);

  function handleFilters() {
    setShowFilters(!showFilters);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }

  function handleNew() {
    navigate(RoutesConstants.registrationRegisterSupplier);
  }

  function handleClear() {
    reset(INITIAL_FORM_STATE);
    if (statusRegisterReducer.statusRegister.data !== null) {
      const keyStatusRegister = String(StatusRegisterEnum.Active);
      const valueStatusRegister = String(
        statusRegisterReducer.statusRegister.data?.find(
          x => x.key === keyStatusRegister
        )?.value
      );
      setValue('statusRegister', {
        key: keyStatusRegister,
        value: valueStatusRegister
      });
    }
    setGridData(INITIAL_GRID_STATE);
    setFormFilters(null);
    setPageIndex(PaginationEnum.PageIndexIntial);
    setHasShowMore(false);
    setIsLoadingInfiniteScroll(false);

    if (optionDefaultCountryDrop) {
      callSupplierApiDomain(optionDefaultCountryDrop);
    } else {
      dispatch(resetReducerSuppliers());
    }
  }

  function callSupplierApiDomain(optionSelected: KeyValue | null) {
    // Clean fields & reducers
    setValue('company', null);
    dispatch(resetReducerSuppliers());

    if (optionSelected !== null) {
      const request: SupplierFilterRequest = {
        guidCountry: String(optionSelected.key),
        type: HeadQuarterBranchFilterEnum.All
      };
      dispatch(getSuppliers(request));
    }
  }

  function onSubmit(formInputs: ISearchSupplierForm) {
    setPageIndex(PaginationEnum.PageIndexIntial);
    setGridData({
      isLoading: true,
      totalItems: 0,
      data: []
    });

    const requestForm: SupplierFilterRequest = {
      guidCountry: String(formInputs.country?.key),
      guidSupplier: formInputs.company?.key
        ? String(formInputs.company.key)
        : null,
      registrationNumber: formInputs.rfc,
      idStatusRegister: formInputs.statusRegister?.key
        ? Number(formInputs.statusRegister.key)
        : null,
      type: HeadQuarterBranchFilterEnum.All,
      pageIndex: PaginationEnum.PageIndexIntial,
      pageSize: PaginationEnum.PageSize30
    };

    setFormFilters(requestForm);
    const requestFormatted = removeEmptyValues(requestForm);

    supplierApi.getSearchGrid(requestFormatted).then(response => {
      setGridData({
        isLoading: false,
        totalItems: response.data.totalNumberOfRecords,
        data: response.data.results
      });
      setHasShowMore(
        response.data.results.length < response.data.totalNumberOfRecords
      );
    });
    setShowFilters(!showFilters);
  }

  function onLoadInfiniteScroll(request: SupplierFilterRequest) {
    setIsLoadingInfiniteScroll(true);
    supplierApi
      .getSearchGrid(request)
      .then(response => {
        let resultData: SupplierDataResponse[] = [];
        let resultTotalItems: number = 0;
        if (
          response.data &&
          response.data.results &&
          response.data.results.length > 0
        ) {
          resultData = response.data.results;
          resultTotalItems = response.data.totalNumberOfRecords;
        }
        if (gridData.data.length > 0) {
          resultData = [...gridData.data, ...resultData];
        }
        setGridData({
          isLoading: false,
          totalItems: resultTotalItems,
          data: resultData
        });

        // Disable show more
        if (
          resultTotalItems < PaginationEnum.PageSize30 ||
          resultTotalItems === resultData.length
        ) {
          setHasShowMore(false);
        }
      })
      .finally(() => setIsLoadingInfiniteScroll(false));
  }

  const canCreateNew = userIsAdmin() || userIsInsurer();

  return (
    <ContainerScreen
      hasBackground
      title={`
      ${getTranslateByKey('information.registration')}
      ${' > '}
      ${getTranslateByKey('information.searchSupplier')}`}>
      <S.Container>
        <AreaButton>
          <Button onClick={handleFilters} id="id-searchSupplier-btn-filter">
            {getTranslateByKey('information.filters')}
          </Button>
          {canCreateNew && (
            <Button onClick={handleNew} id="id-searchSupplier-btn-newSupplier">
              {getTranslateByKey('information.newSupplier')}
            </Button>
          )}
        </AreaButton>
        <Box
          showBox={showFilters}
          title={getTranslateByKey('information.filters')}>
          <form onSubmit={customHandleSubmit} noValidate>
            <S.RowInputs>
              <KogAutoCompleteControlled
                required
                label={getTranslateByKey('information.country')}
                control={control}
                options={countryReducer.countriesUser.data}
                isLoading={countryReducer.countriesUser.isLoading}
                nameCustom="country"
                rulesCustom={{ required: true }}
                errorMessageCustom={
                  errors.country?.type === 'required'
                    ? getTranslateByKey('information.countryRequired', true)
                    : ''
                }
                hasErrorCustom={!!errors.country}
                onChangeCustom={(optionSelected: KeyValue | null) => {
                  callSupplierApiDomain(optionSelected);
                }}
                autoHighlightCustom={!!optionDefaultCountryDrop}
                setValueCustom={setValue}
                disabled={optionDefaultCountryDrop !== null}
                id="id-searchSupplier-txt-country"
              />
              <KogAutoCompleteControlled
                label={getTranslateByKey('information.company')}
                control={control}
                nameCustom="company"
                rulesCustom={{ required: false }}
                errorMessageCustom=""
                hasErrorCustom={false}
                options={supplierReducer.suppliers.data}
                isLoading={supplierReducer.suppliers.isLoading}
                onChangeCustom={(optionSelected: KeyValue | null) => { }}
                disabled={
                  isDisabledSupplierDrop() || !!optionDefaultSupplierDrop
                }
                autoHighlightCustom={!!optionDefaultSupplierDrop}
                setValueCustom={setValue}
                id="id-searchSupplier-txt-company"
              />
              <KogInputTextControlledNew
                label={getTranslateByKey('information.rfc')}
                helperText={
                  errors.rfc?.type === 'minLength'
                    ? getTranslateByKey(
                      'information.rfcMustHaveAtLeast12Digits',
                      true
                    )
                    : ''
                }
                name="rfc"
                error={!!errors.rfc}
                inputProps={{ maxLength: 20 }}
                controllerProps={{ rules: { minLength: 12 } }}
                control={control}
                id="id-searchSupplier-txt-rfc"
              />
            </S.RowInputs>
            <S.RowInputs>
              <KogAutoCompleteControlled
                label={getTranslateByKey('information.status')}
                nameCustom="statusRegister"
                valueDefaultKey={String(StatusRegisterEnum.Active)}
                errorMessageCustom=""
                control={control}
                setValueCustom={setValue}
                options={statusRegisterReducer.statusRegister.data}
                isLoading={statusRegisterReducer.statusRegister.isLoading}
                rulesCustom={{ required: false }}
                hasErrorCustom={false}
                onChangeCustom={() => { }}
                id="id-searchSupplier-txt-status"
              />
            </S.RowInputs>
            <KOGAreaButtons>
              <SecondaryButton
                type="submit"
                id="id-searchSupplier-btn-search">
                {getTranslateByKey('information.search')}
              </SecondaryButton>
              <SecondaryButton
                onClick={handleClear}
                id="id-searchSupplier-btn-clear">
                {getTranslateByKey('information.clear')}
              </SecondaryButton>
            </KOGAreaButtons>
          </form>
        </Box>

        <SupplierSearchGrid
          isLoadingInfiniteScroll={isLoadingInfiniteScroll}
          pageIndex={pageIndex}
          setPageIndex={setPageIndex}
          hasShowMore={hasShowMore}
          gridData={gridData}
          formFilters={formFilters}
          setFormFilters={setFormFilters}
          getGridData={(request: SupplierFilterRequest) => {
            onLoadInfiniteScroll(request);
          }}
        />
      </S.Container>
    </ContainerScreen>
  );
}

export default withQueryparams(SupplierScreen);
